import React, { ReactNode } from "react";
import { ErrorBoundary } from "@appsignal/react";
import Appsignal from "@appsignal/javascript";
import { plugin as pluginBreadcrumbsNetwork } from "@appsignal/plugin-breadcrumbs-network";
import { plugin as pluginPathDecorator } from "@appsignal/plugin-path-decorator";
import { plugin as pluginWindowEvents } from "@appsignal/plugin-window-events";
import { env, isDevelopment } from "@/env";

type LogErrorParams = {
  event: string;
  args: object;
  error: any;
};

type LogErrorListParams = {
  event: string;
  args: object;
  errors: readonly any[];
};

interface MonitorErrorBoundaryProps {
  fallback?: ((error?: Error) => ReactNode) | ReactNode;
  children: ReactNode;
}

interface IMonitor {
  logError: (params: LogErrorParams) => void;
  logErrorList: (params: LogErrorListParams) => void;
  ErrorBoundary: React.FC<MonitorErrorBoundaryProps>;
}
function createLogErrorList(logErrorFn: (params: LogErrorParams) => void) {
  return function ({ event, args, errors }: LogErrorListParams) {
    errors.forEach((error) => {
      logErrorFn({ event, args, error });
    });
  };
}

function localMonitor(): IMonitor {
  const logError = ({ event, args, error }: LogErrorParams) => {
    console.error({ event, args, error });
  };

  const logErrorList = createLogErrorList(logError);

  // This boundary does nothing special except pass children through
  const Boundary: IMonitor["ErrorBoundary"] = ({ children }) => {
    return <>{children}</>;
  };

  return {
    logError,
    logErrorList,
    ErrorBoundary: Boundary,
  };
}

function appsignalMonitor(): IMonitor {
  if (!env.APPSIGNAL_KEY) {
    console.warn("APPSIGNAL_KEY not found in environment variables, falling back to local monitor");
    return localMonitor();
  }

  const appsignal = new Appsignal({
    key: env.APPSIGNAL_KEY,
    revision: env.VERSION,
  });

  appsignal.use(pluginBreadcrumbsNetwork);
  appsignal.use(pluginPathDecorator);
  appsignal.use(pluginWindowEvents);

  const logError = ({ event, args, error }: LogErrorParams) => {
    appsignal.sendError(error, (span) => {
      span.setAction(event);
      span.setParams(args);
    });
  };

  const logErrorList = createLogErrorList(logError);

  const Boundary: IMonitor["ErrorBoundary"] = ({ fallback, children }) => {
    let fallbackFunc: ((error?: Error) => ReactNode) | undefined;

    if (typeof fallback === "function") {
      fallbackFunc = fallback as (error?: Error) => ReactNode;
    } else if (fallback !== undefined) {
      fallbackFunc = () => fallback;
    }

    return (
      <ErrorBoundary instance={appsignal} fallback={fallbackFunc}>
        {children}
      </ErrorBoundary>
    );
  };

  return {
    logError,
    logErrorList,
    ErrorBoundary: Boundary,
  };
}

const monitor: IMonitor = isDevelopment() ? localMonitor() : appsignalMonitor();

export default monitor;
